import { Tabs } from 'antd';
import { ShieldAlert } from 'lucide-react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { DocumentTypesPanel } from './DocumentTypesPanel';
import { TrailerTypesPanel } from './TrailerTypesPanel';
import { TruckClassesPanel } from './TruckClassesPanel';
import { useDocumentTypes } from './use-document-types';
import { useTrailerTypes } from './use-trailer-types';
import { useTruckClasses } from './use-truck-classes';

function TruckClassesTab({ hook }: { hook: ReturnType<typeof useTruckClasses> }) {
  return (
    <TruckClassesPanel
      classes={hook.state.status === 'loaded' ? hook.state.classes : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function TrailerTypesTab({ hook }: { hook: ReturnType<typeof useTrailerTypes> }) {
  return (
    <TrailerTypesPanel
      types={hook.state.status === 'loaded' ? hook.state.types : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function DocumentTypesTab({ hook }: { hook: ReturnType<typeof useDocumentTypes> }) {
  return (
    <DocumentTypesPanel
      types={hook.state.status === 'loaded' ? hook.state.types : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

/**
 * Fleet vocabulary: truck classes, trailer types and document types, in one
 * tabbed screen — the shape later reference-data milestones (job types,
 * zones, check templates, service intervals) land in too, rather than
 * growing the Admin nav by one entry each.
 */
export function ReferenceDataPage() {
  const truckClasses = useTruckClasses();
  const trailerTypes = useTrailerTypes();
  const documentTypes = useDocumentTypes();
  const states = [truckClasses.state, trailerTypes.state, documentTypes.state];

  if (states.some((s) => s.status === 'forbidden')) return <Navigate to="/" replace />;

  return (
    <>
      <PageHeader eyebrow="Admin" title="Reference data" description="The fleet vocabulary trucks, trailers and documents are described with." />
      {states.some((s) => s.status === 'error') ? (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load reference data"
          description="Something went wrong loading the reference-data catalogues. Try reloading the page."
        />
      ) : (
        <Tabs
          items={[
            { key: 'truck-classes', label: 'Truck classes', children: <TruckClassesTab hook={truckClasses} /> },
            { key: 'trailer-types', label: 'Trailer types', children: <TrailerTypesTab hook={trailerTypes} /> },
            { key: 'document-types', label: 'Document types', children: <DocumentTypesTab hook={documentTypes} /> },
          ]}
        />
      )}
    </>
  );
}
